<template>
  <div class="feature-overview">
    <div class="features-grid">
      <div
        v-for="(feature, index) in features"
        :key="index"
        class="feature-item"
        :class="{ highlight: feature.highlight }"
      >
        <div v-if="feature.badge" class="feature-badge">
          {{ feature.badge }}
        </div>
        <div class="feature-header">
          <div class="feature-icon">
            <FontAwesome :icon="['fas', feature.icon]" />
          </div>
          <strong class="feature-title">{{ feature.title }}</strong>
        </div>
        <p class="feature-description">
          {{ feature.description }}
        </p>
      </div>
    </div>
    <div class="missing-features" v-if="props.showMissingAFeature">
      <p class="missing-features-title">
        {{ $t("website.features.are_you_missing_a_feature") }}
      </p>
      <p class="missing-features-text">
        {{ $t("website.features.share_your_ideas_and_suggestions") }}
      </p>
      <a class="cta-button" href="/contact">
        {{ $t("website.features.contact_us") }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
// Translation
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const props = defineProps({
  showMissingAFeature: Boolean,
  highlightFirst: Boolean,
});

// Array of features with image, title, and description
const features = computed(() => [
  {
    icon: "stopwatch",
    title: t("website.features.featurelist.timetracking"),
    description: t("website.features.featurelist.timetracking_description"),
    highlight: props.highlightFirst,
    badge: props.highlightFirst ? "Core Feature" : null,
  },
  {
    icon: "clock",
    title: t("website.features.featurelist.customers_timelogs"),
    description: t(
      "website.features.featurelist.customers_timelogs_description",
    ),
  },
  {
    icon: "tasks",
    title: t("website.features.featurelist.projects"),
    description: t("website.features.featurelist.projects_description"),
  },
  {
    icon: "user-friends",
    title: t("website.features.featurelist.multi_user_support"),
    description: t(
      "website.features.featurelist.multi_user_support_description",
    ),
  },
  {
    icon: "file-invoice-dollar",
    title: t("website.features.featurelist.billing_invoicing"),
    description: t(
      "website.features.featurelist.billing_invoicing_description",
    ),
  },
  {
    icon: "chart-line",
    title: t("website.features.featurelist.detailed_analytics"),
    description: t(
      "website.features.featurelist.detailed_analytics_description",
    ),
  },
  {
    icon: "users",
    title: t("website.features.featurelist.user_management"),
    description: t("website.features.featurelist.user_management_description"),
  },
  {
    icon: "file-alt",
    title: t("website.features.featurelist.reports"),
    description: t("website.features.featurelist.reports_description"),
  },
  {
    icon: "calendar-alt",
    title: t("website.features.featurelist.calendar"),
    description: t("website.features.featurelist.calendar_description"),
  },
  {
    icon: "file-invoice",
    title: t("website.features.featurelist.billing"),
    description: t("website.features.featurelist.billing_description"),
  },
  {
    icon: "money-bill-wave",
    title: t("website.features.featurelist.hourly_rates_rules"),
    description: t(
      "website.features.featurelist.hourly_rates_rules_description",
    ),
  },
  {
    icon: "question-circle",
    title: t("website.features.featurelist.support"),
    description: t("website.features.featurelist.support_description"),
  },
]);
</script>

<style scoped>
.feature-overview {
  max-width: 1200px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.feature-item {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  padding: 40px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  border-color: #ff6b35;
}

.feature-item.highlight {
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: white;
  border: none;
}

.feature-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  backdrop-filter: blur(10px);
}

.feature-icon {
  width: 60px;
  height: 60px;
  background: #f3f4f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ff6b35;
  margin-bottom: 20px;
}

.feature-item.highlight .feature-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.feature-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #1f2937;
}

.feature-item.highlight .feature-title {
  color: white;
}

.feature-description {
  color: #6b7280;
  line-height: 1.6;
  font-size: 1rem;
  text-align: justify;
}

.feature-item.highlight .feature-description {
  color: rgba(255, 255, 255, 0.9);
}

.missing-features {
  margin-top: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.missing-features-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.missing-features-text {
  max-width: 600px;
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.cta-button {
  display: inline-block;
  font-size: 1rem;
  padding: 14px 32px;
  background: linear-gradient(135deg, #ff6b35 0%, #ffa94d 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  font-weight: 600;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.4);
}

.cta-button:active {
  transform: scale(0.98);
}

@media (max-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
  }
}
</style>
